import { useForm } from '@tanstack/react-form'
import { useNavigate, useParams } from '@tanstack/react-router'
import { InvalidTokenMessage } from '../components/InvalidTokenMessage'
import mccLogo from '@/assets/mcc_logo.png'
import { Container } from '@/components/Container'
import LoadingButton from '@/components/custom/loading-button'
import PasswordInput from '@/components/custom/password-input'
import { Spinner } from '@/components/Spinner'
import { Button } from '@/components/ui/button'
import { useCheckSetupTokenQuery } from '@/features/auth/hooks'
import { ResetPasswordSchema } from '@/features/auth/schema/passwordSchema'
import { useCompleteUserSetupMutation } from '@/features/users/hooks/useUserCompleteSetupMutation'
import { completeSetupRoute } from '@/routes'

export default function CompleteSetup() {
  const navigate = useNavigate()
  const setupToken = useParams({ from: completeSetupRoute.id }).token || ''

  const { mutateAsync: completeSetupMutation, isPending: isSetupPending } =
    useCompleteUserSetupMutation()

  const {
    data,
    error,
    isLoading: isChecking,
  } = useCheckSetupTokenQuery(setupToken, 'setup')

  const form = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      await completeSetupMutation({
        token: setupToken,
        password: value.newPassword,
      })
      navigate({ to: '/login' })
    },
  })

  if (isChecking) {
    return (
      <Container>
        <Spinner size={48} />
      </Container>
    )
  }

  if (data?.isVerified) {
    return (
      <Container>
        <div className="flex flex-col items-center gap-6">
          <img
            src={mccLogo}
            alt="Maranatha Christian Church"
            className="w-48 sm:w-52"
          />
          <div className="w-full max-w-sm rounded-lg border bg-white p-6 shadow-sm">
            <h1 className="mb-4 text-xl font-semibold">
              Account Already Verified
            </h1>
            <div className="mb-4">
              Your email has already been verified. You can log in now.
            </div>
            <Button
              className="w-full"
              onClick={() => navigate({ to: '/login' })}
            >
              Go to Login
            </Button>
          </div>
        </div>
      </Container>
    )
  }

  if (error) {
    return <InvalidTokenMessage error={error} type="setup" />
  }

  return (
    <Container>
      <div className="flex flex-col items-center gap-6">
        <img
          src={mccLogo}
          alt="Maranatha Christian Church"
          className="w-48 sm:w-52"
        />
        <div className="w-full max-w-sm rounded-lg border bg-white p-6 shadow-sm">
          <h1 className="mb-4 text-xl font-semibold">Verify Account</h1>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-3"
          >
            <form.Field
              name="newPassword"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    ResetPasswordSchema.innerType().shape.newPassword.safeParse(
                      value,
                    )
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => (
                <div className="gap-0">
                  <PasswordInput
                    placeholder="••••••••"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="pr-10"
                    showStrengthIndicator
                    field={field}
                    label="New Password"
                  />
                </div>
              )}
            />
            <form.Field
              name="confirmPassword"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    ResetPasswordSchema.innerType().shape.confirmPassword.safeParse(
                      value,
                    )
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => (
                <div className="gap-0 mt-6 mb-8">
                  <PasswordInput
                    placeholder="••••••••"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="pr-10"
                    field={field}
                    label="Confirm Password"
                  />
                </div>
              )}
            />
            <form.Subscribe
              selector={(state) => [state.canSubmit]}
              children={([canSubmit]) => (
                <LoadingButton
                  type="submit"
                  className="w-full"
                  isLoading={isSetupPending}
                  disabled={!canSubmit || isSetupPending}
                >
                  {isSetupPending ? 'Setting up...' : 'Complete Setup'}
                </LoadingButton>
              )}
            />
          </form>
        </div>
      </div>
    </Container>
  )
}
